import React, { useState } from "react";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Tooltip,
  Collapse,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";

const COLORS = {
  color: "#4F46E5", // Accent Text
  hover: "#60A5FA", // Hover
  textPrimary: "#0A0F2C", // Primary Text
  textSecondary: "#475569", // Secondary Text
  links: "#1E3A8A", // Links
};

function Register() {
  const [formData, setFormData] = useState({
    registrationType: "individual", // company | individual
    email: "",
    password: "",
    reenterpassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    companyName: "",
    companyDomain: "",
    companyWebsite: "",
    businessRegistrationNumber: "",
    role: "candidate",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companyVerificationDoc, setCompanyVerificationDoc] = useState(null);
  const [gstCertificate, setGstCertificate] = useState(null);
  const [panCard, setPanCard] = useState(null);
  const [professionalTax, setProfessionalTax] = useState(null);
  const [epfoRegistration, setEpfoRegistration] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
const [passwordTouched, setPasswordTouched] = useState(false);
  // ✅ Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success"); // success | error
  const [duplicateEmailOpen, setDuplicateEmailOpen] = useState(false);
  const [duplicatePhoneOpen, setDuplicatePhoneOpen] = useState(false);

  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Google-onboarding mode: recruiter completed Google auth, now submitting company docs
  const isGoogleOnboarding = new URLSearchParams(location.search).get("googleOnboarding") === "1";

  // Pre-fill form from Google data if in onboarding mode
  React.useEffect(() => {
    if (!isGoogleOnboarding) return;
    try {
      const raw = sessionStorage.getItem("google_onboarding_data");
      if (!raw) return;
      const gData = JSON.parse(raw);
      setFormData(prev => ({
        ...prev,
        registrationType: "company",
        email:     gData.email     || prev.email,
        firstName: gData.firstName || prev.firstName,
        lastName:  gData.lastName  || prev.lastName,
        role:      "admin",
      }));
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleOnboarding]);

  const isCompany = formData.registrationType === "company";

  // ---------------- VALIDATION ----------------
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[+]?[0-9\s-]{10,15}$/;

  function getPasswordStrength(password) {
      if (!password) return { level: 0, label: '', barColor: '#e0e0e0', textColor: '#9e9e9e', percent: 0 };
      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasNum = /[0-9]/.test(password);
      const hasSpecial = /[^A-Za-z0-9]/.test(password);
      const len = password.length;
      if (len >= 12 && hasUpper && hasLower && hasNum && hasSpecial) {
        return { level: 4, label: 'Very Strong', barColor: '#1b5e20', textColor: '#1b5e20', percent: 100 };
      }
      if (len >= 8 && hasUpper && hasLower && hasNum && hasSpecial) {
        return { level: 3, label: 'Strong', barColor: '#4caf50', textColor: '#2e7d32', percent: 75 };
      }
      if (len >= 6 && ((hasUpper && hasLower) || (hasNum && hasLower) || (hasNum && hasUpper))) {
        return { level: 2, label: 'Medium', barColor: '#ff9800', textColor: '#e65100', percent: 50 };
      }
      return { level: 1, label: 'Weak', barColor: '#ef5350', textColor: '#c62828', percent: 25 };
  }

  function validateField(name, value) {
    let error = "";

    if (name === "firstName" && !value.trim()) {
      error = "First name is required";
    }

    if (name === "lastName" && !value.trim()) {
      error = "Last name is required";
    }

    if (name === "companyName" && isCompany && !value.trim()) {
      error = "Company name is required";
    }

    if (name === "companyWebsite" && isCompany && !String(value || "").trim()) {
      error = "Company website is required";
    }

    if (name === "companyDomain" && isCompany && !String(value || "").trim()) {
      error = "Domain (Industry) is required";
    }

    if (name === "email" && !emailRegex.test(value)) {
      error = "Enter a valid email address";
    }

    if (name === "phone") {
      if (!value.trim()) {
        error = isCompany ? "Contact phone is required" : "Phone is required";
      } else if (value && !phoneRegex.test(value)) {
        error = "Enter a valid phone number";
      }
    }

    if (name === "address") {
      if (!value.trim()) {
        error = isCompany ? "Company address is required" : "Address is required";
      } else if (value && value.trim().length < 5) {
        error = "Address must be at least 5 characters";
      }
    }

    if (name === "password" && !value) {
      error = "Password is required";
    }

    return error;
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));

    if (name === "password") {
      setPasswordTouched(true);
    }
  }

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setCompanyVerificationDoc(file);
    if (file) {
      setErrors((prev) => ({ ...prev, companyVerificationDoc: "" }));
    }
  }

  function handleGstFileChange(e) {
    const file = e.target.files[0] || null;
    setGstCertificate(file);
    if (file) setErrors((prev) => ({ ...prev, gstCertificate: "" }));
  }

  function handlePanFileChange(e) {
    const file = e.target.files[0] || null;
    setPanCard(file);
    if (file) setErrors((prev) => ({ ...prev, panCard: "" }));
  }

  function handleProfessionalTaxFileChange(e) {
    const file = e.target.files[0] || null;
    setProfessionalTax(file);
    if (file) setErrors((prev) => ({ ...prev, professionalTax: "" }));
  }

  function handleEpfoFileChange(e) {
    const file = e.target.files[0] || null;
    setEpfoRegistration(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const newErrors = {
      firstName: validateField("firstName", formData.firstName),
      lastName: validateField("lastName", formData.lastName),
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
      phone: validateField("phone", formData.phone),
      address: validateField("address", formData.address),
      ...(isCompany
        ? {
            companyName: validateField("companyName", formData.companyName),
            companyDomain: validateField("companyDomain", formData.companyDomain),
            companyWebsite: validateField("companyWebsite", formData.companyWebsite),
          }
        : {}),
    };

    if (isCompany && !companyVerificationDoc) {
      newErrors.companyVerificationDoc = "Company verification document is required";
    }

    if (companyVerificationDoc && companyVerificationDoc.size > 5 * 1024 * 1024) {
      newErrors.companyVerificationDoc = "File size must not exceed 5MB";
    }

    if (isCompany && !gstCertificate) {
      newErrors.gstCertificate = "GST Registration Certificate is required";
    }
    if (gstCertificate && gstCertificate.size > 5 * 1024 * 1024) {
      newErrors.gstCertificate = "File size must not exceed 5MB";
    }

    if (isCompany && !panCard) {
      newErrors.panCard = "PAN Card of Company is required";
    }
    if (panCard && panCard.size > 5 * 1024 * 1024) {
      newErrors.panCard = "File size must not exceed 5MB";
    }

    if (isCompany && !professionalTax) {
      newErrors.professionalTax = "Professional Tax Registration document is required";
    }
    if (professionalTax && professionalTax.size > 5 * 1024 * 1024) {
      newErrors.professionalTax = "File size must not exceed 5MB";
    }

    if (epfoRegistration && epfoRegistration.size > 5 * 1024 * 1024) {
      newErrors.epfoRegistration = "File size must not exceed 5MB";
    }

    if (formData.password !== formData.reenterpassword) {
      newErrors.reenterpassword = "Passwords do not match";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
      } else {
            const pwChecks = [
              formData.password.length >= 8,
              /[A-Z]/.test(formData.password),
              /[a-z]/.test(formData.password),
              /[0-9]/.test(formData.password),
              /[^A-Za-z0-9]/.test(formData.password),
            ];
            if (!pwChecks.every(Boolean)) {
              newErrors.password = "Password does not meet security requirements";
            }
    }

    // In Google-onboarding mode, skip password validation entirely
    if (isGoogleOnboarding) {
      delete newErrors.password;
      delete newErrors.reenterpassword;
    }

    if (Object.values(newErrors).some((err) => err)) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      let payload;
      if (isCompany) {
        payload = new FormData();
        Object.keys(formData).forEach((key) => {
          if (formData[key] !== undefined && formData[key] !== "") {
            payload.append(key, formData[key]);
          }
        });
        payload.append("companyVerificationDoc", companyVerificationDoc);
        payload.append("gstCertificate", gstCertificate);
        payload.append("panCard", panCard);
        payload.append("professionalTax", professionalTax);
        if (epfoRegistration) {
          payload.append("epfoRegistration", epfoRegistration);
        }
        // Pass Google identity for onboarding path
        if (isGoogleOnboarding) {
          try {
            const gData = JSON.parse(sessionStorage.getItem("google_onboarding_data") || "{}");
            if (gData.googleId)  payload.append("googleId",      gData.googleId);
            if (gData.picture)   payload.append("googlePicture", gData.picture);
            // Use a placeholder password — backend will generate a random one
            payload.append("password", "__google_onboarding__");
          } catch (_) { /* ignore */ }
        }
      } else {
        payload = formData;
      }

      await register(payload);

      if (isGoogleOnboarding) {
        sessionStorage.removeItem("google_onboarding_data");
      }

      setModalType("success");
      setModalMessage(
        isGoogleOnboarding
          ? "Company profile submitted! Your account is under review. You will be notified once approved."
          : "Registration successful! Please check your email to verify your account."
      );
      setModalOpen(true);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;

      if (status === 409 && msg === "Phone number already registered") {
        setDuplicatePhoneOpen(true);
      } else if (status === 409) {
        setDuplicateEmailOpen(true);
      } else {
        setModalType("error");
        if (status === 400 || msg === "email is not exist") {
          setModalMessage("Email is not exist");
        } else {
          setModalMessage(msg || "Failed to register");
        }
        setModalOpen(true);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleModalClose = () => {
    setModalOpen(false);
    if (modalType === "success") {
      navigate("/login");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 4, md: 8 } }}>
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "16px",
            border: "1px solid rgba(79, 70, 229, 0.2)",
            boxShadow: "0 10px 40px rgba(79, 70, 229, 0.15), 0 20px 60px rgba(96, 165, 250, 0.12)",
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="h4"
            mb={3}
            align="center"
            sx={{
              color: "#0A0F2C",
              fontSize: { xs: "1.75rem", md: "2.125rem" },
              fontWeight: 700,
              textTransform: "capitalize",
            }}
            gutterBottom
          >
            {formData.registrationType === "company"
              ? "start hiring smarter"
              : "start your career with exihire"}
          </Typography>

          {errors.form && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.form}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Please select how you would like to proceed with your registration.
              </Typography>

              <FormControl>
                <RadioGroup
                  row
                  name="registrationType"
                  value={formData.registrationType}
                  onChange={handleChange}
                  sx={{ gap: 6, flexWrap: "wrap" }}
                >
                  <FormControlLabel
                    value="company"
                    control={<Radio />}
                    label="Company / Business"
                  />
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label="Individual"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Box>

            {isCompany ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                  />

                  <TextField
                    select
                    label="Domain (Industry)"
                    name="companyDomain"
                    value={formData.companyDomain}
                    onChange={handleChange}
                    required
                    error={!!errors.companyDomain}
                    helperText={errors.companyDomain}
                  >
                    {[
                      "Software / IT",
                      "Human Resources (HR)",
                      "Finance / Banking",
                      "Healthcare",
                      "Education / EdTech",
                      "E-commerce",
                      "Manufacturing",
                      "Telecommunications",
                      "Consulting",
                      "Marketing / Advertising",
                      "Real Estate",
                      "Legal Services",
                      "Media / Entertainment",
                      "Logistics / Supply Chain",
                      "Retail",
                      "Automotive",
                      "Construction",
                      "Pharmaceutical",
                      "Energy / Utilities",
                      "Agriculture",
                      "Hospitality / Travel",
                      "Government / Public Sector",
                      "Non-Profit / NGO",
                      "Other",
                    ].map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <TextField
                  label="Company Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
            ) : (
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            )}

            {/* Password */}
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setPasswordTouched(true)}
              margin="normal"
              required
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onMouseDown={() => setShowPassword(true)}
                      onMouseUp={() => setShowPassword(false)}
                      onMouseLeave={() => setShowPassword(false)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements */}
                        {(() => {
                          const pw = formData.password;
                          const rules = [
                            { label: "Minimum 8 characters", met: pw.length >= 8 },
                            { label: "At least 1 uppercase letter (A-Z)", met: /[A-Z]/.test(pw) },
                            { label: "At least 1 lowercase letter (a-z)", met: /[a-z]/.test(pw) },
                            { label: "At least 1 number (0-9)", met: /[0-9]/.test(pw) },
                            { label: "At least 1 special character (!@#$%^&*)", met: /[^A-Za-z0-9]/.test(pw) },
                          ];
                          const allMet = rules.every(r => r.met);
                          const strengthInfo = getPasswordStrength(pw);
                          const isDefault = pw.length === 0;

                          return (
                            <>
                              <Collapse in={passwordTouched || pw.length > 0}>
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 0.5,
                                    p: 2,
                                    borderRadius: "10px",
                                    border: "1px solid",
                                    borderColor: allMet ? "#a7f3d0" : "#e2e8f0",
                                    background: allMet ? "rgba(167,243,208,0.07)" : "#fafafa",
                                    transition: "border-color 0.3s ease, background 0.3s ease",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#374151",
                                      display: "block",
                                      mb: 1,
                                      fontSize: "0.77rem",
                                      letterSpacing: "0.03em",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Password Requirements
                                  </Typography>
                                  {rules.map((rule, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        mb: i < rules.length - 1 ? 0.5 : 0,
                                      }}
                                    >
                                      {isDefault ? (
                                        <RadioButtonUnchecked sx={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }} />
                                      ) : rule.met ? (
                                        <CheckCircle sx={{ fontSize: 13, color: "#16a34a", flexShrink: 0 }} />
                                      ) : (
                                        <Cancel sx={{ fontSize: 13, color: "#dc2626", flexShrink: 0 }} />
                                      )}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.78rem",
                                          color: isDefault ? "#6b7280" : rule.met ? "#16a34a" : "#dc2626",
                                          fontWeight: isDefault ? 400 : 500,
                                          transition: "color 0.2s ease",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {rule.label}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>

                              {/* Strength Meter */}
                              {pw.length > 0 && (
                                <Box sx={{ mt: 1, mb: 0.5 }}>
                                  <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                                    {[1, 2, 3, 4].map((seg) => (
                                      <Box
                                        key={seg}
                                        sx={{
                                          flex: 1,
                                          height: 5,
                                          borderRadius: 3,
                                          backgroundColor:
                                            seg <= strengthInfo.level ? strengthInfo.barColor : "#e5e7eb",
                                          transition: "background-color 0.35s ease",
                                        }}
                                      />
                                    ))}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: strengthInfo.textColor,
                                      fontWeight: 700,
                                      fontSize: "0.74rem",
                                      transition: "color 0.35s ease",
                                    }}
                                  >
                                    {strengthInfo.label}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          );
                        })()}

            {/* Confirm Password */}
            <TextField
              fullWidth
              label="Re-Enter Password"
              name="reenterpassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.reenterpassword}
              onChange={handleChange}
              margin="normal"
              required
              error={
                             !!errors.reenterpassword ||
                             (formData.reenterpassword.length > 0 &&
                               formData.password !== formData.reenterpassword)
                           }
              helperText={errors.reenterpassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onMouseDown={() => setShowConfirmPassword(true)}
                      onMouseUp={() => setShowConfirmPassword(false)}
                      onMouseLeave={() => setShowConfirmPassword(false)}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Live confirm password match indicator */}
                        {formData.reenterpassword.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: -0.5, mb: 0.5 }}>
                            {formData.password === formData.reenterpassword ? (
                              <CheckCircle sx={{ fontSize: 14, color: "#16a34a" }} />
                            ) : (
                              <Cancel sx={{ fontSize: 14, color: "#dc2626" }} />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.78rem",
                                color:
                                  formData.password === formData.reenterpassword ? "#16a34a" : "#dc2626",
                                fontWeight: 600,
                                transition: "color 0.2s ease",
                              }}
                            >
                              {formData.password === formData.reenterpassword
                                ? "Passwords match"
                                : "Passwords do not match"}
                            </Typography>
                          </Box>
                        )}


            {isCompany ? (
              <>
                <TextField
                  fullWidth
                  label="Company Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  required
                  error={!!errors.address}
                  helperText={errors.address}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TextField
                    label="Company Website"
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleChange}
                    required
                  />

                  <TextField
                    label="Contact Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    error={!!errors.phone}
                    helperText={errors.phone || "e.g. +1 234 567 8900"}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Business Registration Number (optional)"
                  name="businessRegistrationNumber"
                  value={formData.businessRegistrationNumber}
                  onChange={handleChange}
                  margin="normal"
                />

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Company License / Registration / Identification Document *
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ whiteSpace: "nowrap", borderColor: errors.companyVerificationDoc ? "error.main" : undefined }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color={companyVerificationDoc ? "text.primary" : "text.secondary"}
                      sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {companyVerificationDoc ? companyVerificationDoc.name : "No file chosen"}
                    </Typography>
                  </Box>
                  {errors.companyVerificationDoc ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {errors.companyVerificationDoc}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    GST Registration Certificate *
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ whiteSpace: "nowrap", borderColor: errors.gstCertificate ? "error.main" : undefined }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleGstFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color={gstCertificate ? "text.primary" : "text.secondary"}
                      sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {gstCertificate ? gstCertificate.name : "No file chosen"}
                    </Typography>
                  </Box>
                  {errors.gstCertificate ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {errors.gstCertificate}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    PAN Card of Company *
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ whiteSpace: "nowrap", borderColor: errors.panCard ? "error.main" : undefined }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handlePanFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color={panCard ? "text.primary" : "text.secondary"}
                      sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {panCard ? panCard.name : "No file chosen"}
                    </Typography>
                  </Box>
                  {errors.panCard ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {errors.panCard}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Professional Tax Registration *
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ whiteSpace: "nowrap", borderColor: errors.professionalTax ? "error.main" : undefined }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleProfessionalTaxFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color={professionalTax ? "text.primary" : "text.secondary"}
                      sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {professionalTax ? professionalTax.name : "No file chosen"}
                    </Typography>
                  </Box>
                  {errors.professionalTax ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {errors.professionalTax}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    EPFO Registration{" "}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (Optional)
                    </Typography>
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleEpfoFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color={epfoRegistration ? "text.primary" : "text.secondary"}
                      sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {epfoRegistration ? epfoRegistration.name : "No file chosen"}
                    </Typography>
                  </Box>
                  {errors.epfoRegistration ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {errors.epfoRegistration}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                  required
                  error={!!errors.phone}
                  helperText={errors.phone || "e.g. +1 234 567 8900"}
                />

                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  required
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </>
            )}

           {(() => {
                         const pw = formData.password;
                         const allMet = [
                           pw.length >= 8,
                           /[A-Z]/.test(pw),
                           /[a-z]/.test(pw),
                           /[0-9]/.test(pw),
                           /[^A-Za-z0-9]/.test(pw),
                         ].every(Boolean);
                         const pwMatch =
                           formData.reenterpassword.length > 0 &&
                           pw === formData.reenterpassword;
                         const isPasswordValid = allMet && pwMatch;
                         return (
                           <Tooltip
                             title={
                               !isPasswordValid && !loading
                                 ? "Please complete all password requirements"
                                 : ""
                             }
                             placement="top"
                             arrow
                           >
                             <span style={{ display: "block", marginTop: 24 }}>
                               <Button
                                 type="submit"
                                 fullWidth
                                 variant="contained"
                                 size="large"
                                 disabled={loading || !isPasswordValid}
                                 sx={{
                                   background:
                                     loading || !isPasswordValid
                                       ? undefined
                                       : "linear-gradient(90deg, #4F46E5 0%, #4338CA 100%)",
                                   transition: "all 0.2s ease",
                                   py: 1.5,
                                   fontWeight: 700,
                                   fontSize: "1rem",
                                 }}
                               >
                                 {loading ? "Registering..." : "Register"}
                               </Button>
                             </span>
                           </Tooltip>
                         );
                       })()}
          </form>

          {/* ── Google Sign-Up (individual / candidate only, only when Client ID configured) ── */}
          {process.env.REACT_APP_GOOGLE_CLIENT_ID && formData.registrationType !== "company" && !isGoogleOnboarding && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  or sign up with
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
              </Box>
              <GoogleSignInButton
                onCredential={async (credential) => {
                  try {
                    setLoading(true);
                    const result = await googleLogin(credential, "candidate");
                    if (result && result.requiresEmailOtp) {
                      sessionStorage.setItem("otp_temp_token",   result.tempToken);
                      sessionStorage.setItem("otp_masked_email",  result.maskedEmail || "");
                      sessionStorage.setItem("otp_return_path", "/candidate/dashboard");
                      navigate("/verify-email-otp");
                      return;
                    }
                    navigate("/candidate/dashboard");
                  } catch (err) {
                    setErrors(prev => ({ ...prev, form: err.response?.data?.message || "Google Sign-Up failed. Please try again." }));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                text="signup_with"
              />
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "#1E3A8A" }}>
                Click here to login
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Duplicate Email Dialog */}
      <Dialog
        open={duplicateEmailOpen}
        onClose={() => setDuplicateEmailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0, pt: 3 }}>
          <ErrorOutline sx={{ fontSize: 56, color: "#d32f2f", display: "block", mx: "auto", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Registration Failed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", px: 3, pt: 1 }}>
          <DialogContentText sx={{ color: "#4a4a4a", fontSize: "0.95rem", lineHeight: 1.7 }}>
            This email address is already associated with an existing account.
            <br /><br />
            Please login using your existing account or use a different email address.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            onClick={() => setDuplicateEmailOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#4F46E5",
              color: "#4F46E5",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { borderColor: "#006b6b", backgroundColor: "rgba(0,139,139,0.04)" },
            }}
          >
            Try Another Email
          </Button>
          <Button
            onClick={() => { setDuplicateEmailOpen(false); navigate("/login"); }}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #4F46E5 0%, #4338CA 100%)",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #006b6b 0%, #005a4b 100%)" },
            }}
          >
            Go To Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Phone Dialog */}
      <Dialog
        open={duplicatePhoneOpen}
        onClose={() => setDuplicatePhoneOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0, pt: 3 }}>
          <ErrorOutline sx={{ fontSize: 56, color: "#d32f2f", display: "block", mx: "auto", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Registration Failed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", px: 3, pt: 1 }}>
          <DialogContentText sx={{ color: "#4a4a4a", fontSize: "0.95rem", lineHeight: 1.7 }}>
            This phone number is already associated with an existing account.
            <br /><br />
            Please login using your existing account or use a different phone number.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            onClick={() => setDuplicatePhoneOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#4F46E5",
              color: "#4F46E5",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { borderColor: "#006b6b", backgroundColor: "rgba(0,139,139,0.04)" },
            }}
          >
            Try Another Phone Number
          </Button>
          <Button
            onClick={() => { setDuplicatePhoneOpen(false); navigate("/login"); }}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #4F46E5 0%, #4338CA 100%)",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #006b6b 0%, #005a4b 100%)" },
            }}
          >
            Go To Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Modal */}
      <Dialog open={modalOpen} onClose={handleModalClose}>
        <DialogTitle>
          {modalType === "success" ? "Success" : "Error"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {modalMessage}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleModalClose}
            variant="contained"
            color={modalType === "success" ? "success" : "error"}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Register;
